import React from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  HStack,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { 
  FaTrophy, 
  FaTicketAlt, 
  FaCoins, 
  FaUsers,
  FaCalendarAlt,
  FaLock,
  FaInfoCircle,
  FaPercentage
} from 'react-icons/fa';

/**
 * BaseBountyPool Component
 * 
 * A flexible foundation for all scratch card bounty pools that displays:
 * - Prize pool statistics
 * - Recent claims
 * - RTP information
 * - Randomness verification
 * 
 * This component is designed to be extended by specific scratch card games.
 */
const BaseBountyPool = ({
  // Bounty pool data
  poolData = {
    totalPrizePool: 0,
    claimedPrizes: 0,
    rtp: 75,
    recentClaims: []
  },
  // Custom styling
  accentColor = "blue.400",
  bgColor = "blue.900",
  // Custom column headers
  columnHeaders = {
    remaining: "REMAIN",
    odds: "ODDS",
    prize: "PRIZE"
  },
  // Prize color mapping
  prizeColorMap = null,
  // Custom glass styles
  customGlassStyles = null,
  // Custom content
  renderHeader,
  renderStats,
  renderFooter
}) => {
  // Calculate prize pool percentage claimed
  const percentClaimed = poolData.totalPrizePool > 0 
    ? (poolData.claimedPrizes / poolData.totalPrizePool) * 100 
    : 0;
  
  // Format timestamp to readable date
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  // Glassmorphism styles - use custom styles if provided, otherwise use default blue styles
  const defaultGlassBox = {
    background: "rgba(13, 41, 85, 0.7)", // Blue shade with transparency
    backdropFilter: "blur(10px)",
    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
  };
  
  const defaultGlassBoxHighlight = {
    ...defaultGlassBox,
    background: "rgba(25, 63, 121, 0.7)", // Slightly lighter blue
  };
  
  const defaultGlassBoxDark = {
    ...defaultGlassBox,
    background: "rgba(9, 24, 51, 0.8)", // Darker blue
  };
  
  // Use custom glass styles if provided, otherwise use defaults
  const glassBox = customGlassStyles ? customGlassStyles.glassBox : defaultGlassBox;
  const glassBoxHighlight = customGlassStyles ? customGlassStyles.glassBoxHighlight : defaultGlassBoxHighlight;
  const glassBoxDark = customGlassStyles ? customGlassStyles.glassBoxDark : defaultGlassBoxDark;
  
  return (
    <Box 
      bg={bgColor} 
      borderRadius="xl" 
      p={6} 
      boxShadow="xl"
      mt={6}
      mb={6}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: typeof bgColor === 'string' && bgColor.includes('linear-gradient')
          ? bgColor // Use the provided gradient directly
          : typeof bgColor === 'string' && bgColor.startsWith('#') 
            ? `linear-gradient(135deg, ${bgColor}, rgba(9, 43, 22, 0.9))` // For Cash King's green
            : `linear-gradient(135deg, ${bgColor}, rgba(9, 24, 51, 0.9))`, // For other cards' blue
        zIndex: -1,
      }}
    >
      <Heading as="h3" size="lg" mb={4} color={accentColor}>
        <Icon as={FaTrophy} mr={2} />
        Bounty Pool
      </Heading>
      
      {/* Top Stats with Glassmorphism */}
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={6}>
        <Box sx={glassBoxHighlight} p={4} borderRadius="lg" transition="all 0.3s" _hover={{ transform: "translateY(-5px)", boxShadow: "0 12px 32px 0 rgba(0, 0, 0, 0.5)" }}>
          <Flex direction="column" align="center" justify="center" height="100%">
            <Icon as={FaPercentage} color="purple.300" fontSize="2xl" mb={2} />
            <Text color="gray.200" fontSize="sm" fontWeight="bold" textAlign="center">Overall RTP</Text>
            <Heading size="md" color="white" mt={1}>90%</Heading>
            <VStack spacing={0} mt={1}>
              <Badge colorScheme="blue" width="100%" textAlign="center">75% Monetary</Badge>
              <Badge colorScheme="green" width="100%" textAlign="center">+15% Bonuses</Badge>
            </VStack>
          </Flex>
        </Box>
        
        <Box sx={glassBoxHighlight} p={4} borderRadius="lg" transition="all 0.3s" _hover={{ transform: "translateY(-5px)", boxShadow: "0 12px 32px 0 rgba(0, 0, 0, 0.5)" }}>
          <Flex direction="column" align="center" justify="center" height="100%">
            <Icon as={FaCoins} color="yellow.300" fontSize="2xl" mb={2} />
            <Text color="gray.200" fontSize="sm" fontWeight="bold" textAlign="center">Prize Pool</Text>
            <Heading size="md" color="white" mt={1}>{poolData.totalPrizePool?.toLocaleString() || "1,500,000"} JINU</Heading>
            <Text color="yellow.400" fontSize="xs" mt={1} textAlign="center">
              +{poolData.usdValue?.toLocaleString() || "15,000"} USD
            </Text>
          </Flex>
        </Box>
        
        <Box sx={glassBox} p={4} borderRadius="lg" transition="all 0.3s" _hover={{ transform: "translateY(-5px)", boxShadow: "0 12px 32px 0 rgba(0, 0, 0, 0.5)" }}>
          <Flex direction="column" align="center" justify="center" height="100%">
            <Icon as={FaTicketAlt} color="blue.300" fontSize="2xl" mb={2} />
            <Text color="gray.200" fontSize="sm" fontWeight="bold" textAlign="center">Cards Minted</Text>
            <Heading size="md" color="white" mt={1}>{poolData.cardsMinted?.toLocaleString() || "4,632"}</Heading>
            <Text color="blue.400" fontSize="xs" mt={1} textAlign="center">
              {poolData.cardsRemaining?.toLocaleString() || "5,368"} remaining
            </Text>
          </Flex>
        </Box>
        
        <Box sx={glassBox} p={4} borderRadius="lg" transition="all 0.3s" _hover={{ transform: "translateY(-5px)", boxShadow: "0 12px 32px 0 rgba(0, 0, 0, 0.5)" }}>
          <Flex direction="column" align="center" justify="center" height="100%">
            <Icon as={FaUsers} color="green.300" fontSize="2xl" mb={2} />
            <Text color="gray.200" fontSize="sm" fontWeight="bold" textAlign="center">Prizes Claimed</Text>
            <Heading size="md" color="white" mt={1}>{poolData.claimedPrizes?.toLocaleString() || "350,000"} JINU</Heading>
            <Text color="green.400" fontSize="xs" mt={1} textAlign="center">
              {poolData.winners?.toLocaleString() || "432"} winners
            </Text>
          </Flex>
        </Box>
        
        <Box sx={glassBox} p={4} borderRadius="lg" transition="all 0.3s" _hover={{ transform: "translateY(-5px)", boxShadow: "0 12px 32px 0 rgba(0, 0, 0, 0.5)" }}>
          <Flex direction="column" align="center" justify="center" height="100%">
            <Icon as={FaTrophy} color="orange.300" fontSize="2xl" mb={2} />
            <Text color="gray.200" fontSize="sm" fontWeight="bold" textAlign="center">Top Prizes Left</Text>
            <Heading size="md" color="white" mt={1}>5</Heading>
            <VStack spacing={0} mt={1}>
              <Badge 
                bg={prizeColorMap && prizeColorMap[100000] ? 
                  `linear-gradient(135deg, ${prizeColorMap[100000]}, ${prizeColorMap[100000].split('.')[0]}.600)` : 
                  "linear-gradient(135deg, gold, yellow.600)"}
                color="white"
                width="100%" 
                textAlign="center"
                boxShadow="0 0 5px rgba(255, 255, 255, 0.2)"
              >
                1x 100K JINU
              </Badge>
              <Badge 
                bg={prizeColorMap && prizeColorMap[50000] ? 
                  `linear-gradient(135deg, ${prizeColorMap[50000]}, ${prizeColorMap[50000].split('.')[0]}.600)` : 
                  "linear-gradient(135deg, yellow.400, yellow.600)"}
                color="white"
                width="100%" 
                textAlign="center"
                boxShadow="0 0 5px rgba(255, 255, 255, 0.2)"
              >
                4x 50K JINU
              </Badge>
            </VStack>
          </Flex>
        </Box>
      </SimpleGrid>
      
      {/* Progress Bars with Glassmorphism */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
        <Box sx={glassBox} p={4} borderRadius="lg">
          <Heading as="h4" size="sm" mb={3} color="white">Minting Progress</Heading>
          <Progress 
            value={(poolData.cardsMinted / poolData.totalCards) * 100 || 46.32} 
            colorScheme="purple" 
            size="md" 
            borderRadius="full"
            bg="whiteAlpha.300"
            mb={2}
            sx={{
              '& > div': {
                background: 'linear-gradient(90deg, #6B46C1, #9F7AEA)',
                boxShadow: '0 0 10px rgba(159, 122, 234, 0.5)'
              }
            }}
          />
          <Flex justify="space-between">
            <Text color="gray.200" fontSize="sm">{poolData.cardsMinted?.toLocaleString() || "4,632"} minted</Text>
            <Text color="gray.200" fontSize="sm">{poolData.totalCards?.toLocaleString() || "10,000"} total</Text>
          </Flex>
        </Box>
        
        <Box sx={glassBox} p={4} borderRadius="lg">
          <Heading as="h4" size="sm" mb={3} color="white">Prize Claims Progress</Heading>
          <Progress 
            value={percentClaimed || 23.33} 
            colorScheme="green" 
            size="md" 
            borderRadius="full"
            bg="whiteAlpha.300"
            mb={2}
            sx={{
              '& > div': {
                background: 'linear-gradient(90deg, #38A169, #68D391)',
                boxShadow: '0 0 10px rgba(104, 211, 145, 0.5)'
              }
            }}
          />
          <Flex justify="space-between">
            <Text color="gray.200" fontSize="sm">{poolData.claimedPrizes?.toLocaleString() || "350,000"} JINU claimed</Text>
            <Text color="gray.200" fontSize="sm">{poolData.totalPrizePool?.toLocaleString() || "1,500,000"} JINU total</Text>
          </Flex>
        </Box>
      </SimpleGrid>
      
      {/* Prize Distribution and Recent Claims with Glassmorphism */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
        <Box sx={glassBoxDark} p={4} borderRadius="lg">
          <Heading as="h4" size="sm" mb={4} color="white">Prize Distribution</Heading>
          <Box 
            borderRadius="lg" 
            overflow="hidden"
            sx={{
              background: "rgba(9, 24, 51, 0.5)",
              backdropFilter: "blur(5px)",
            }}
          >
            <Table variant="simple" size="sm" layout="fixed">
              <Thead position="sticky" top={0} zIndex={1} sx={{
                background: "linear-gradient(90deg, rgba(13, 41, 85, 0.9), rgba(25, 63, 121, 0.9))",
                backdropFilter: "blur(10px)",
              }}>
                <Tr>
                  <Th color="blue.100" fontSize="xs" fontWeight="bold">PRIZE</Th>
                  <Th color="blue.100" fontSize="xs" fontWeight="bold" isNumeric>TOTAL</Th>
                  <Th color="blue.100" fontSize="xs" fontWeight="bold" isNumeric>CLAIMED</Th>
                  <Th color="blue.100" fontSize="xs" fontWeight="bold" isNumeric>{columnHeaders.remaining}</Th>
                  <Th color="blue.100" fontSize="xs" fontWeight="bold" isNumeric>ODDS</Th>
                  <Th color="blue.100" fontSize="xs" fontWeight="bold" isNumeric>ENTRIES</Th>
                </Tr>
              </Thead>
              <Tbody>
                {poolData.prizeDistribution.map((prize, index) => {
                  // Determine badge color based on prize value - more distinct colors for each prize amount
                  let badgeColor;
                  let gradientBg = false;
                  
                  // Extract the prize amount as a number
                  const prizeText = prize.prize;
                  let prizeAmount = 0;
                  
                  // Log for debugging
                  console.log(`Processing prize: ${prizeText}`);
                  
                  if (prizeText.includes('K')) {
                    // Handle K notation (e.g., 100K)
                    prizeAmount = parseFloat(prizeText.replace(/[^0-9.]/g, '')) * 1000;
                  } else if (prizeText.includes('JINU')) {
                    // Handle format like "100,000 JINU"
                    // Remove all non-numeric characters (including commas)
                    prizeAmount = parseInt(prizeText.replace(/[^0-9]/g, ''));
                  } else {
                    // Handle regular numbers
                    prizeAmount = parseFloat(prizeText.replace(/[^0-9.]/g, ''));
                  }
                  
                  // Log the extracted amount for debugging
                  console.log(`Extracted amount: ${prizeAmount}`);
                  
                  // Force specific colors for known prize amounts to ensure they display correctly
                  if (prizeText.includes('100,000')) {
                    prizeAmount = 100000;
                  } else if (prizeText.includes('50,000')) {
                    prizeAmount = 50000;
                  } else if (prizeText.includes('25,000')) {
                    prizeAmount = 25000;
                  } else if (prizeText.includes('10,000')) {
                    prizeAmount = 10000;
                  } else if (prizeText.includes('5,000')) {
                    prizeAmount = 5000;
                  }
                  
                  // Use prizeColorMap if provided, otherwise use default colors
                  console.log(`Looking up color for amount: ${prizeAmount}`);
                  if (prizeColorMap && prizeColorMap[prizeAmount]) {
                    badgeColor = prizeColorMap[prizeAmount];
                    console.log(`Found color in map: ${badgeColor}`);
                    // Apply gradient background for top prizes
                    gradientBg = prizeAmount >= 10000;
                  } else {
                    console.log(`No color found in map for: ${prizeAmount}`);

                    // Default color logic based on prize amount instead of text matching
                    if (prizeAmount >= 100000) {
                      badgeColor = "gold";
                      gradientBg = true;
                    } else if (prizeAmount >= 50000) {
                      badgeColor = "yellow.400";
                      gradientBg = true;
                    } else if (prizeAmount >= 25000) {
                      badgeColor = "orange.400";
                      gradientBg = true;
                    } else if (prizeAmount >= 10000) {
                      badgeColor = "red.400";
                      gradientBg = true;
                    } else if (prizeAmount >= 5000) {
                      badgeColor = "pink.400";
                      gradientBg = true;
                    } else if (prizeAmount >= 2000) {
                      badgeColor = "purple.400";
                    } else if (prizeAmount >= 1000) {
                      badgeColor = "blue.400";
                    } else if (prizeAmount >= 500) {
                      badgeColor = "cyan.400";
                    } else if (prizeAmount >= 400) {
                      badgeColor = "teal.400";
                    } else if (prizeAmount >= 300) {
                      badgeColor = "green.400";
                    } else if (prizeAmount >= 200) {
                      badgeColor = "green.500";
                    } else if (prizeAmount >= 100) {
                      badgeColor = "blue.500";
                    } else {
                      badgeColor = "blue.300"; // Default for other prizes
                    }
                  }
                  
                  return (
                    <Tr key={index} _hover={{ bg: 'blackAlpha.400' }}>
                      <Td>
                        <Badge 
                          px={2}
                          py={0.5}
                          borderRadius="md"
                          fontSize="xs"
                          fontWeight="bold"
                          textTransform="uppercase"
                          boxShadow="0 0 5px rgba(255, 255, 255, 0.2)"
                          color="white"
                          bg={(() => {
                            // Rainbow gradient with vibrant colors and slight transparency
                            if (prizeText.includes('100,000')) {
                              return 'linear-gradient(135deg, rgba(180, 0, 180, 0.85), rgba(128, 0, 128, 0.7))'; // Vibrant purple (top prize)
                            } else if (prizeText.includes('50,000')) {
                              return 'linear-gradient(135deg, rgba(180, 43, 226, 0.85), rgba(138, 43, 226, 0.7))'; // Vibrant blue-violet
                            } else if (prizeText.includes('25,000')) {
                              return 'linear-gradient(135deg, rgba(110, 0, 180, 0.85), rgba(75, 0, 130, 0.7))'; // Vibrant indigo
                            } else if (prizeText.includes('10,000')) {
                              return 'linear-gradient(135deg, rgba(30, 30, 255, 0.85), rgba(0, 0, 255, 0.7))'; // Vibrant blue
                            } else if (prizeText.includes('5,000')) {
                              return 'linear-gradient(135deg, rgba(30, 158, 255, 0.85), rgba(0, 128, 255, 0.7))'; // Vibrant azure
                            } else if (prizeText.includes('2,000')) {
                              return 'linear-gradient(135deg, rgba(30, 255, 255, 0.85), rgba(0, 255, 255, 0.7))'; // Vibrant cyan
                            } else if (prizeText.includes('1,000')) {
                              return 'linear-gradient(135deg, rgba(30, 255, 158, 0.85), rgba(0, 255, 128, 0.7))'; // Vibrant spring green
                            } else if (prizeText.includes('500')) {
                              return 'linear-gradient(135deg, rgba(30, 255, 30, 0.85), rgba(0, 255, 0, 0.7))'; // Vibrant green
                            } else if (prizeText.includes('400')) {
                              return 'linear-gradient(135deg, rgba(158, 255, 30, 0.85), rgba(128, 255, 0, 0.7))'; // Vibrant chartreuse
                            } else if (prizeText.includes('300')) {
                              return 'linear-gradient(135deg, rgba(255, 255, 30, 0.85), rgba(255, 255, 0, 0.7))'; // Vibrant yellow
                            } else if (prizeText.includes('200')) {
                              return 'linear-gradient(135deg, rgba(255, 158, 30, 0.85), rgba(255, 128, 0, 0.7))'; // Vibrant orange
                            } else if (prizeText.includes('100')) {
                              return 'linear-gradient(135deg, rgba(255, 30, 30, 0.85), rgba(255, 0, 0, 0.7))'; // Vibrant red (lowest prize)
                            } else {
                              return 'linear-gradient(135deg, rgba(158, 158, 158, 0.85), rgba(128, 128, 128, 0.7))'; // Vibrant gray (fallback)
                            }
                          })()} 
                          fontWeight="bold" 
                          textShadow="0px 1px 2px rgba(0,0,0,0.5)" 
                          backdropFilter="blur(2px)" 
                          border="1px solid rgba(255, 255, 255, 0.2)"
                        >
                          {prize.prize}
                        </Badge>
                      </Td>
                      <Td isNumeric color="white">{prize.total}</Td>
                      <Td isNumeric color="green.300">{prize.claimed}</Td>
                      <Td isNumeric color="blue.300">{prize.remaining}</Td>
                      <Td isNumeric color="yellow.300">{prize.odds}</Td>
                      <Td isNumeric color="purple.300">{prize.entries || Math.ceil(10000 / parseInt(prize.odds.split(':')[1]))}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Box>
        
        <Box sx={glassBoxDark} p={4} borderRadius="lg">
          <Heading as="h4" size="sm" mb={4} color="white">Recent Claims</Heading>
          <Box 
            borderRadius="lg" 
            overflow="hidden"
            maxHeight="300px"
            overflowY="auto"
            sx={{
              background: "rgba(9, 24, 51, 0.5)",
              backdropFilter: "blur(5px)",
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
                background: 'rgba(0, 0, 0, 0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: accentColor,
                borderRadius: '24px',
              },
            }}
          >
            <Table variant="simple" size="sm">
              <Thead position="sticky" top={0} zIndex={1} sx={{
                background: "linear-gradient(90deg, rgba(13, 41, 85, 0.9), rgba(25, 63, 121, 0.9))",
                backdropFilter: "blur(10px)",
              }}>
                <Tr>
                  <Th color="blue.100" fontSize="xs" fontWeight="bold">PLAYER</Th>
                  <Th color="blue.100" fontSize="xs" fontWeight="bold">PRIZE</Th>
                  <Th color="blue.100" fontSize="xs" fontWeight="bold">TIME</Th>
                </Tr>
              </Thead>
              <Tbody>
                {poolData.recentClaims.map((claim, index) => {
                  // Determine badge color based on prize value
                  let badgeColor = "blue";
                  if (claim.type === 'GIFT BOX') {
                    badgeColor = "orange";
                  } else if (claim.type === '100K') {
                    badgeColor = "purple";
                  } else if (claim.type === '10K') {
                    badgeColor = "teal";
                  } else if (claim.type === '1K') {
                    badgeColor = "blue";
                  } else {
                    badgeColor = "green";
                  }
                  
                  // Format time
                  const minutes = Math.floor((Date.now() - claim.timestamp) / (1000 * 60));
                  const timeText = `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
                  
                  return (
                    <Tr key={index} _hover={{ bg: 'blackAlpha.400' }}>
                      <Td>
                        <Badge colorScheme={badgeColor} variant="solid" px={2} py={0.5}>
                          {claim.player}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={badgeColor} variant="solid" px={2} py={0.5}>
                          {claim.type}
                        </Badge>
                      </Td>
                      <Td>
                        <Text color="gray.300" fontSize="xs">{timeText}</Text>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </SimpleGrid>
      
      {/* Prize Summary with Glassmorphism */}
      <Box sx={glassBox} p={5} borderRadius="lg" mb={6}>
        <Heading as="h4" size="sm" mb={4} color="white">Prize Summary</Heading>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          <Box 
            sx={{
              background: "linear-gradient(135deg, rgba(234, 179, 8, 0.2), rgba(234, 179, 8, 0.05))",
              backdropFilter: "blur(5px)",
              borderRadius: "lg",
              padding: 4,
              borderWidth: "1px",
              borderColor: "rgba(234, 179, 8, 0.3)",
              boxShadow: "0 4px 12px rgba(234, 179, 8, 0.15)",
              transition: "all 0.3s",
              _hover: { transform: "translateY(-3px)", boxShadow: "0 6px 16px rgba(234, 179, 8, 0.25)" }
            }}
          >
            <Flex direction="column" align="center" textAlign="center">
              <Heading size="xs" color="yellow.400" mb={2}>Top Prizes</Heading>
              <Text color="white" fontSize="xl" fontWeight="bold">
                {poolData.prizeSummary?.topPrizes?.count || 12} cards
              </Text>
              <Text color="yellow.200" fontSize="sm" mt={1}>
                {poolData.prizeSummary?.topPrizes?.amount?.toLocaleString() || "350,000"} JINU
              </Text>
            </Flex>
          </Box>
          
          <Box 
            sx={{
              background: "linear-gradient(135deg, rgba(79, 209, 197, 0.2), rgba(79, 209, 197, 0.05))",
              backdropFilter: "blur(5px)",
              borderRadius: "lg",
              padding: 4,
              borderWidth: "1px",
              borderColor: "rgba(79, 209, 197, 0.3)",
              boxShadow: "0 4px 12px rgba(79, 209, 197, 0.15)",
              transition: "all 0.3s",
              _hover: { transform: "translateY(-3px)", boxShadow: "0 6px 16px rgba(79, 209, 197, 0.25)" }
            }}
          >
            <Flex direction="column" align="center" textAlign="center">
              <Heading size="xs" color="cyan.400" mb={2}>Mid-Tier Prizes</Heading>
              <Text color="white" fontSize="xl" fontWeight="bold">
                {poolData.prizeSummary?.midPrizes?.count || 420} cards
              </Text>
              <Text color="cyan.200" fontSize="sm" mt={1}>
                {poolData.prizeSummary?.midPrizes?.amount?.toLocaleString() || "265,000"} JINU
              </Text>
            </Flex>
          </Box>
          
          <Box 
            sx={{
              background: "linear-gradient(135deg, rgba(72, 187, 120, 0.2), rgba(72, 187, 120, 0.05))",
              backdropFilter: "blur(5px)",
              borderRadius: "lg",
              padding: 4,
              borderWidth: "1px",
              borderColor: "rgba(72, 187, 120, 0.3)",
              boxShadow: "0 4px 12px rgba(72, 187, 120, 0.15)",
              transition: "all 0.3s",
              _hover: { transform: "translateY(-3px)", boxShadow: "0 6px 16px rgba(72, 187, 120, 0.25)" }
            }}
          >
            <Flex direction="column" align="center" textAlign="center">
              <Heading size="xs" color="green.400" mb={2}>Lower Prizes</Heading>
              <Text color="white" fontSize="xl" fontWeight="bold">
                {poolData.prizeSummary?.lowPrizes?.count || 4200} cards
              </Text>
              <Text color="green.200" fontSize="sm" mt={1}>
                {poolData.prizeSummary?.lowPrizes?.amount?.toLocaleString() || "720,000"} JINU
              </Text>
            </Flex>
          </Box>
          
          <Box 
            sx={{
              background: "linear-gradient(135deg, rgba(159, 122, 234, 0.2), rgba(159, 122, 234, 0.05))",
              backdropFilter: "blur(5px)",
              borderRadius: "lg",
              padding: 4,
              borderWidth: "1px",
              borderColor: "rgba(159, 122, 234, 0.3)",
              boxShadow: "0 4px 12px rgba(159, 122, 234, 0.15)",
              transition: "all 0.3s",
              _hover: { transform: "translateY(-3px)", boxShadow: "0 6px 16px rgba(159, 122, 234, 0.25)" }
            }}
          >
            <Flex direction="column" align="center" textAlign="center">
              <Heading size="xs" color="purple.400" mb={2}>Win Odds</Heading>
              <Text color="white" fontSize="xl" fontWeight="bold">
                {poolData.prizeSummary?.odds?.anyWin || "1 in 2.15"}
              </Text>
              <Text color="purple.200" fontSize="sm" mt={1}>
                Top Prize: {poolData.prizeSummary?.odds?.topPrize || "1 in 10,000"}
              </Text>
            </Flex>
          </Box>
        </SimpleGrid>
      </Box>
      
      <Divider mb={6} />
      
      {/* Randomness Verification with Glassmorphism */}
      <Box sx={glassBox} p={5} borderRadius="lg" mb={6}>
        <Heading as="h4" size="md" mb={4} color="white" display="flex" alignItems="center">
          <Icon as={FaLock} color="cyan.300" mr={2} />
          Randomness Verification
        </Heading>
        
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box 
            sx={{
              background: "rgba(9, 24, 51, 0.6)",
              backdropFilter: "blur(5px)",
              borderRadius: "lg",
              padding: 4,
              borderWidth: "1px",
              borderColor: "rgba(255, 255, 255, 0.1)",
            }}
          >
            <Text color="white" fontWeight="bold" mb={2}>Provably Fair System</Text>
            <Text color="gray.300" fontSize="sm" mb={3}>
              All game outcomes are determined using verifiable random functions on the blockchain.
              This ensures complete transparency and fairness for all players.
            </Text>
            
            <Flex align="center" mt={4}>
              <Icon as={FaInfoCircle} color="blue.300" mr={2} />
              <Text color="blue.300" fontSize="sm" fontWeight="bold">Verification Available On-Chain</Text>
            </Flex>
          </Box>
          
          <Box 
            sx={{
              background: "rgba(9, 24, 51, 0.6)",
              backdropFilter: "blur(5px)",
              borderRadius: "lg",
              padding: 4,
              borderWidth: "1px",
              borderColor: "rgba(255, 255, 255, 0.1)",
            }}
          >
            <Text color="white" fontWeight="bold" mb={2}>Latest Block Hash</Text>
            <Box 
              p={3} 
              borderRadius="md" 
              bg="rgba(0, 0, 0, 0.3)" 
              fontFamily="mono"
              fontSize="xs"
              color="green.300"
              mb={2}
              overflowX="auto"
              css={{
                '&::-webkit-scrollbar': {
                  height: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0, 0, 0, 0.1)',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: accentColor,
                  borderRadius: '24px',
                },
              }}
            >
              0x7a31fe{poolData.blockHash || "5d8c6e9b3a2f1d4e8c7b6a5d9e8f7c6b5a4d3e2f1c"}
            </Box>
            <Flex justify="space-between" align="center">
              <Text color="gray.400" fontSize="xs">Block: #{poolData.blockNumber || "18,456,932"}</Text>
              <Text color="gray.400" fontSize="xs">Updated: {poolData.lastVerified || "2 mins ago"}</Text>
            </Flex>
          </Box>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default BaseBountyPool;
